"use client";
import {useCallback, useEffect, useState} from "react";
import InfiniteScroll from "@/components/InfiniteScroll";
import CampaignList from "@/components/views/campaings/CampaignList";
import { CampaignsLoading } from "@/components/views/campaings/Campaigns";
import apiFetch from "@/lib/fetch";
import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import NotFound from "@/components/views/list/NotFound";
import HeaderDataCount from "@/components/views/common/HeaderDataCount";
import { useHeader } from "@/hooks/headerContext";
import { useCampaignContext } from "@/context/CampaignContext";
import debounce from "lodash/debounce";
import { useTranslation } from "@/hooks/translationContext";

interface Props extends SearchProps {
  data: any;
}

export default function CampaignClientSection(props: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(props.data);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { clearFilters } = useHeader();
  const { campaignFilters } = useCampaignContext();

  const fetchData = async (page) => {
    let url = `campaign?index=${page}`;

    if (props.service) url += `&service=${props.service}`;
    if (props.district) url += `&district=${props.district}`;
    if (props.venue) url += `&venue=${props.venue}`;

    if (campaignFilters.service)
      url += `&campaignService=${campaignFilters?.service?.id}`;
    if (campaignFilters.district)
      url += `&campaignDistrict=${campaignFilters?.district?.id}`;

    return apiFetch({ route: url });
  };

  const next = async (page: number) => {
    if (isLoading) return;

    setIsLoading(true);
    const response = await fetchData(page);
    setIsLoading(false);

    if (response.response?.length === 0) setHasMore(false);

    setData((prev: any) => {
      return {
        response: [...prev.response, ...response.response],
        summary: prev?.summary,
      };
    });

    if (response.response?.length < DEFAULT_DATA_LIMIT) setHasMore(false);
  };

  useEffect(() => {
    setData(props.data);

    if ((props.data as any).response.length < DEFAULT_DATA_LIMIT)
      setHasMore(false);
    else setHasMore(true);
  }, [props.data]);

  const fetchCampaigns = useCallback(
      debounce(async () => {
        setIsLoading(true);
        const response = await fetchData(0);
        setData(response);

        if (response.response?.length < DEFAULT_DATA_LIMIT) setHasMore(false);

        setIsLoading(false);
      }, 300),
      [campaignFilters]
  );

  useEffect(() => {
    fetchCampaigns();
    return () => {
      fetchCampaigns.cancel();
    };
  }, [campaignFilters, fetchCampaigns]);

  if (!props.data?.response || props.data?.response?.length === 0) {
    return (
      <div className="py-4">
        <HeaderDataCount count={props.data?.summary} />
        <NotFound
          title={t("campaign-not-found-title")}
          searchText={t("campaign-search-placeholder")}
          showAllText={t("campaign-list-all-button")}
          clearFilter={() => clearFilters()}
        />
      </div>
    );
  }

  return (
    <InfiniteScroll
      skeletonElementId="campaign-card-skeleton"
      isLoading={isLoading}
      hasMore={hasMore}
      loading={<CampaignsLoading />}
      next={next}
      dataLength={data.response?.length || 0}
    >
      <section className="grid justify-items-center grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-4 py-4">
        <CampaignList campaigns={data.response} titleHeadingLevel="h2" />
      </section>
    </InfiniteScroll>
  );
}
